import React from 'react'

import {
  PanelHeader,
  PanelTitle
} from 'components/attacks/details/AttackStyles'
import CardTooltip from 'components/common/CardTooltip'
import Popover from 'components/common/Popover'
import Text from 'components/common/Text'
import TextButton from 'components/common/TextButton'
import Icon from 'components/Icon'
import theme from 'theme'
import { pluralize } from 'utils/strings'

type Executions = Record<string, any>[]

const flattenExecutions = (groupedExecutionsByTarget) =>
  groupedExecutionsByTarget.flatMap(
    (groupedExecution) => groupedExecution.executions
  )

const getTotalEventCodes = (executions: Executions) => {
  return executions
    .map((e) => e.event_codes?.length ?? 0)
    .reduce((acc, e) => acc + e, 0)
}

const ExecutionPanelHeader: React.FC<{
  executions: Executions
  kubernetes: boolean
  openExecutionWithInsights: () => void
}> = ({ executions, kubernetes, openExecutionWithInsights }) => {
  const totalEventCodes = kubernetes
    ? getTotalEventCodes(flattenExecutions(executions))
    : getTotalEventCodes(executions)

  return (
    <PanelHeader css={{ justifyContent: 'space-between' }}>
      <PanelTitle>Executions</PanelTitle>

      {totalEventCodes > 0 && (
        <div
          data-test-execution-panel-header-insights
          css={{
            alignItems: 'center',
            display: 'flex',
            fontFamily: theme.fonts.mono
          }}
        >
          <Text as="small">
            {totalEventCodes} {pluralize('insight', totalEventCodes)}
          </Text>
          <Popover>
            {({ getTargetProps, getPopoverProps }) => (
              <>
                <div
                  {...getTargetProps({
                    css: {
                      color: 'inherit',
                      fontSize: theme.fontSizes[2],

                      '&:hover': {
                        cursor: 'pointer'
                      }
                    }
                  })}
                  data-test-execution-panel-header-insights-icon
                  css={{
                    display: 'flex'
                  }}
                >
                  <Icon
                    css={{
                      color: theme.colors.steel[1],
                      paddingLeft: theme.space[1]
                    }}
                    icon="solidCircleInfo"
                  />
                </div>
                <div data-test-execution-panel-header-insights-tooltip>
                  <CardTooltip
                    displayArrow
                    size="small"
                    variation="dark"
                    {...getPopoverProps({
                      css: {
                        maxWidth: 200
                      }
                    })}
                  >
                    <Text
                      as="small"
                      css={{
                        color: theme.colors.white[0],
                        fontFamily: theme.fonts.base
                      }}
                    >
                      Learn about your environment and self diagnose events that
                      happen during attack execution.
                    </Text>
                  </CardTooltip>
                </div>
              </>
            )}
          </Popover>
          <TextButton
            data-test-execution-panel-header-text-button
            css={{
              fontSize: theme.fontSizes[0],
              fontFamily: theme.fonts.mono,
              paddingLeft: theme.space[2]
            }}
            onClick={openExecutionWithInsights}
          >
            <strong>View All</strong>
          </TextButton>
        </div>
      )}
    </PanelHeader>
  )
}

export default ExecutionPanelHeader
